import {
  Box,
  Center,
  IconButton,
  Image,
  Spinner,
  AspectRatio,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { chakra } from "@chakra-ui/react";
import {
  DragControls,
  Reorder,
  useDragControls,
  useMotionValue,
} from "framer-motion";
import { KYDStoryAsset } from "@common/interfaces/KYDEvent";
import { useDialogWithOptions } from "../../../Common/Dialog";
import { withConfirmDialog } from "../ConfirmDialog";
import { useState, useRef, useEffect, useMemo } from "react";
import { Icon } from "@chakra-ui/react";
import { FaPlay } from "react-icons/fa";

const Item = chakra(Reorder.Item, {
  baseStyle: {
    listStyleType: "none",
  },
});

interface DraggableAssetProps {
  asset: KYDStoryAsset;
  onDelete: (asset: KYDStoryAsset) => void;
}

const DraggableAsset = ({ asset, onDelete }: DraggableAssetProps) => {
  const dragControls = useDragControls();
  const y = useMotionValue(0);
  const dialog = useDialogWithOptions();
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const isVideo = Boolean(asset.key.match(/\.(mp4|mov)(?:\?|$)/i));

  useEffect(() => {
    const generateThumbnail = async () => {
      if (asset.url?.match(/\.(mp4|mov|avi|wmv)$/i)) {
        setIsLoading(false);
        setThumbnailLoaded(true);
        return;
      }

      const video = videoRef.current;
      if (!video) {
        console.log("No video ref available");
        setIsLoading(false);
        setThumbnailLoaded(true);
        return;
      }

      // Create a canvas to capture the frame
      const canvas = document.createElement("canvas");

      // Listen for metadata to load to get video dimensions
      video.addEventListener("loadedmetadata", () => {
        console.log("Video metadata loaded");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Seek to the first frame
        video.currentTime = 0;
      });

      // Once we seek, capture the frame
      video.addEventListener("seeked", () => {
        console.log("Video seeked");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          console.log("No canvas context");
          return;
        }

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const thumbnailDataUrl = canvas.toDataURL("image/jpeg");
        setThumbnailUrl(thumbnailDataUrl);
        setIsLoading(false);
        setThumbnailLoaded(true);
      });

      // Handle errors
      video.addEventListener("error", (e) => {
        console.error("Video error:", e);
        setIsLoading(false);
      });

      console.log("Starting video load");
      video.load();
    };

    setIsLoading(true);
    setThumbnailLoaded(false);
    generateThumbnail();
  }, [asset.url]);

  const handleMouseDownReorder = (
    e: React.PointerEvent,
    item: KYDStoryAsset,
    dc: DragControls
  ) => {
    console.log("mouse down");
    dc.start(e);
  };

  const handleDelete = (e: React.MouseEvent, asset: KYDStoryAsset) => {
    e.stopPropagation();
    dialog.open({
      title: "Delete Story Asset?",
      text: "You sure you want to delete this story asset?",
      actions: [
        {
          type: "cancel",
          text: "Cancel",
          buttonProps: {
            backgroundColor: "gray.100",
            color: "gray.800",
            _hover: {
              backgroundColor: "gray.300",
            },
          },
        },
        {
          type: "delete",
          text: "Delete",
          onClick: () => {
            onDelete(asset);
            dialog.close();
          },
          buttonProps: {
            backgroundColor: "black.500",
            color: "white",
          },
        },
      ],
    });
  };

  const renderAssetPreview = useMemo(() => {
    if (isVideo) {
      return (
        <AspectRatio ratio={1} width="100%" height="100%">
          {isLoading ? (
            <Center height="100%">
              <Spinner size="md" color="gray.500" />
            </Center>
          ) : (
            <video
              ref={videoRef}
              src={asset.url}
              poster={thumbnailUrl}
              preload="metadata"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "0.375rem",
              }}
              onContextMenu={(e) => e.preventDefault()}
            />
          )}
        </AspectRatio>
      );
    }

    return (
      <Image
        src={asset.url}
        alt={`Story asset ${asset.position + 1}`}
        width="100%"
        height="100%"
        objectFit="cover"
        borderRadius="md"
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        fallback={
          <Center height="100%">
            <Spinner size="md" color="gray.500" />
          </Center>
        }
        onLoad={() => setThumbnailLoaded(true)}
      />
    );
  }, [asset.url, asset.position, thumbnailUrl, isLoading]);

  return (
    <Item
      value={asset}
      id={asset.key}
      dragListener={false}
      dragControls={dragControls}
      style={{ y }}
      cursor="grab"
      _active={{ cursor: "grabbing" }}
    >
      <Box
        position="relative"
        role="group"
        width="175px"
        height="200px"
        onPointerDown={(e) => {
          handleMouseDownReorder(e, asset, dragControls);
        }}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest("button")) {
            e.stopPropagation();
          }
        }}
      >
        <IconButton
          position="absolute"
          top={1}
          right={1}
          size="sm"
          isRound
          aria-label="Delete asset"
          icon={<DeleteIcon boxSize={3} color="white" />}
          onClick={(e) => handleDelete(e, asset)}
          bg="black"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          _hover={{ bg: "blackAlpha.800" }}
          transition="opacity 0.2s"
          zIndex={2}
          variant="solid"
        />
        {/* Show loading spinner while thumbnail is loading */}
        {!thumbnailLoaded && (
          <Center
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            zIndex={1}
          >
            <Spinner size="lg" color="white" />
          </Center>
        )}

        {/* Add play button overlay for videos */}
        {isVideo && (
          <Center
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={1}
            pointerEvents="none"
          >
            <Icon
              as={FaPlay}
              color="white"
              w={8}
              h={8}
              filter="drop-shadow(0px 0px 2px rgba(0, 0, 0, 0.5))"
            />
          </Center>
        )}
        {renderAssetPreview}
      </Box>
    </Item>
  );
};

export default withConfirmDialog(DraggableAsset);
